import { useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { Link, useLocation } from 'react-router-dom';

import { useInitialData } from 'contexts/InitialDataContext';
import { useLanguage } from 'contexts/LanguageContext';
import { BulkCompletionSummary } from '../components/BulkCompletionSummary';
import { BulkCsvUpload } from '../components/BulkCsvUpload';
import { BulkEpisodePipelineTracker } from '../components/BulkEpisodePipelineTracker';
import { BulkIdeasReviewTable } from '../components/BulkIdeasReviewTable';
import { BulkProgressSummary } from '../components/BulkProgressSummary';
import { BulkSettings } from '../components/BulkSettings';
import { useBulkEpisodeOrchestrator, type ParsedIdea } from '../hooks/useBulkEpisodeOrchestrator';
import { useCheckIdeaUniqueness, type CheckUniquenessResponse } from '../services/uniquenessApi';

export function BulkEpisodeGenerationView() {
  const { state: orchestratorState, start, abort } = useBulkEpisodeOrchestrator();
  const uniquenessMutation = useCheckIdeaUniqueness();
  const { state: initialDataState } = useInitialData();
  const { selectedLanguage, languages } = useLanguage();
  const location = useLocation();

  // Step state
  const [ideas, setIdeas] = useState<ParsedIdea[]>(() => {
    // Accept ideas passed from the Ideas page via navigation state
    const navIdeas = (location.state as { ideas?: ParsedIdea[] } | null)?.ideas;
    return navIdeas ?? [];
  });
  const [uniquenessData, setUniquenessData] = useState<CheckUniquenessResponse | null>(null);
  const [uniquenessSkipped, setUniquenessSkipped] = useState(false);
  const [selectedIndices, setSelectedIndices] = useState<Set<number>>(() => {
    const navIdeas = (location.state as { ideas?: ParsedIdea[] } | null)?.ideas;
    return navIdeas ? new Set(navIdeas.map((i) => i.index)) : new Set();
  });
  const [similarityThreshold, setSimilarityThreshold] = useState(0.85);
  const [concurrency, setConcurrency] = useState(2);
  const [targetLanguages, setTargetLanguages] = useState<string[]>([]);

  const isRunning = orchestratorState.status === 'running';
  const isCompleted = orchestratorState.status === 'completed';

  // Source language comes from the global language dropdown
  const sourceLanguage = selectedLanguage;

  // Initialize target languages with all available languages when initial data loads
  useEffect(() => {
    if (initialDataState.data?.languages && targetLanguages.length === 0) {
      const available = initialDataState.data.languages
        .filter((l) => l.code !== sourceLanguage && l.code !== 'zxx' && l.code !== 'other')
        .map((l) => l.code);
      setTargetLanguages(available);
    }
  }, [initialDataState.data?.languages, sourceLanguage, targetLanguages.length]);

  // Handle CSV parsed
  const handleIdeasParsed = useCallback((parsed: ParsedIdea[]) => {
    setIdeas(parsed);
    setUniquenessData(null);
    setUniquenessSkipped(false);
    setSelectedIndices(new Set(parsed.map((i) => i.index)));
  }, []);

  // Handle uniqueness check
  const handleCheckUniqueness = useCallback(() => {
    const ideaTexts = ideas.map((i) => i.idea);
    uniquenessMutation.mutate(ideaTexts, {
      onSuccess: (data) => {
        setUniquenessData(data);

        // Auto-deselect non-unique ideas based on threshold
        const uniqueIndices = new Set<number>();
        ideas.forEach((idea) => {
          const result = data.results.find((r) => r.ideaIndex === idea.index);
          const batchDup = data.withinBatchDuplicates.find(
            (d) =>
              (d.ideaA === idea.index || d.ideaB === idea.index) &&
              d.similarity >= similarityThreshold
          );
          const isNonUnique =
            (result?.closestMatch && result.closestMatch.similarity >= similarityThreshold) ||
            batchDup;

          if (!isNonUnique) {
            uniqueIndices.add(idea.index);
          }
        });
        setSelectedIndices(uniqueIndices);
        toast.success(`Uniqueness check complete: ${uniqueIndices.size}/${ideas.length} unique`);
      },
      onError: (err) => {
        toast.error(
          `Uniqueness check failed: ${err.message}. You can skip and proceed without checking.`
        );
      },
    });
  }, [ideas, uniquenessMutation.mutate, similarityThreshold]);

  // Skip uniqueness check — allow proceeding without it
  const handleSkipUniqueness = useCallback(() => {
    setUniquenessSkipped(true);
    toast.success('Uniqueness check skipped — all ideas selected');
  }, []);

  // Handle start generation
  const handleStart = useCallback(() => {
    const selectedIdeas = ideas.filter((i) => selectedIndices.has(i.index));
    if (selectedIdeas.length === 0) {
      toast.error('No ideas selected');
      return;
    }
    start(selectedIdeas, selectedLanguage, concurrency, targetLanguages);
  }, [ideas, selectedIndices, selectedLanguage, concurrency, targetLanguages, start]);

  // Beforeunload warning
  useEffect(() => {
    if (!isRunning) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isRunning]);

  const selectedCount = selectedIndices.size;
  const canStart =
    selectedCount > 0 &&
    (uniquenessData !== null || uniquenessSkipped) &&
    !isRunning &&
    !isCompleted;

  // Determine which step we're on for breadcrumb context
  const currentStep = useMemo(() => {
    if (isRunning || isCompleted) return 'generation';
    if (ideas.length === 0) return 'upload';
    return 'review';
  }, [isRunning, isCompleted, ideas.length]);

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <h1 className="text-3xl font-bold text-gray-900">Bulk Episode Generation</h1>
          <span className="rounded-full bg-indigo-100 px-3 py-1 text-sm font-medium text-indigo-700">
            {languages.find((l) => l.code === selectedLanguage)?.name ?? selectedLanguage}
          </span>
        </div>
        <div className="flex items-center gap-4">
          {isRunning && (
            <button
              type="button"
              onClick={abort}
              className="text-red-600 hover:text-red-800 font-medium"
            >
              Abort Generation
            </button>
          )}
          <Link to="/episodes" className="text-blue-600 hover:text-blue-800 font-medium">
            &larr; Back to Episodes
          </Link>
        </div>
      </div>

      <div className="space-y-6">
        {/* Step 1: CSV Upload */}
        {currentStep === 'upload' && (
          <BulkCsvUpload onIdeasParsed={handleIdeasParsed} disabled={isRunning} />
        )}

        {/* Step 1 compact (after upload) */}
        {currentStep !== 'upload' && !isRunning && !isCompleted && (
          <div className="rounded-lg border border-gray-200 bg-white p-4">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-600">
                <strong>{ideas.length}</strong> ideas loaded from CSV
              </div>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={handleCheckUniqueness}
                  disabled={uniquenessMutation.isPending || isRunning || uniquenessSkipped}
                  className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 disabled:opacity-50"
                >
                  {uniquenessMutation.isPending ? 'Checking...' : 'Check Uniqueness'}
                </button>
                {!uniquenessData && !uniquenessSkipped && !uniquenessMutation.isPending && (
                  <button
                    type="button"
                    onClick={handleSkipUniqueness}
                    className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-600 shadow-sm hover:bg-gray-50"
                  >
                    Skip Check
                  </button>
                )}
                <BulkCsvUpload onIdeasParsed={handleIdeasParsed} disabled={isRunning} />
              </div>
            </div>
          </div>
        )}

        {/* Step 2: Review Ideas */}
        {ideas.length > 0 && !isRunning && !isCompleted && (
          <BulkIdeasReviewTable
            ideas={ideas}
            uniquenessData={uniquenessData}
            selectedIndices={selectedIndices}
            onSelectionChange={setSelectedIndices}
            similarityThreshold={similarityThreshold}
            onThresholdChange={setSimilarityThreshold}
            disabled={isRunning}
          />
        )}

        {/* Step 3: Settings + Start */}
        {ideas.length > 0 && !isRunning && !isCompleted && (
          <>
            <BulkSettings
              concurrency={concurrency}
              onConcurrencyChange={setConcurrency}
              targetLanguages={targetLanguages}
              onTargetLanguagesChange={setTargetLanguages}
              sourceLanguage={sourceLanguage}
              disabled={isRunning}
            />

            <div className="flex justify-end">
              <button
                type="button"
                onClick={handleStart}
                disabled={!canStart}
                className="rounded-md bg-indigo-600 px-6 py-3 text-sm font-semibold text-white shadow-sm hover:bg-indigo-700 disabled:opacity-50"
              >
                Start Bulk Generation ({selectedCount} episodes)
              </button>
            </div>
          </>
        )}

        {/* Progress (during/after generation) */}
        {(isRunning || isCompleted) && (
          <>
            <BulkProgressSummary state={orchestratorState} />
            <BulkEpisodePipelineTracker episodes={orchestratorState.episodes} />
          </>
        )}

        {/* Completion summary */}
        {isCompleted && <BulkCompletionSummary state={orchestratorState} />}
      </div>
    </div>
  );
}
